// ─── Central Analytics Module ────────────────────────────────────────────────
//
// Single import point for all tracking. Keeps vendor SDKs out of components.
//
// USAGE
//   import { analytics } from "../analytics";
//   import { EVENTS }    from "../analytics/events";
//
//   analytics.track(EVENTS.PURCHASE, { service_type: "essential" });
//
// ADDING A NEW PROVIDER
//   1. Create src/analytics/providers/<name>.ts following the ga4/metaPixel shape.
//   2. Import it here and add calls in init(), page(), and track() below.
//   3. Add any new env vars to .env (REACT_APP_<PROVIDER>_ID=...).
//
// CONSENT / GDPR
//   Call analytics.setConsent(false) before init() to suppress all tracking
//   (e.g. when the user declines a cookie banner). Call setConsent(true) to
//   re-enable. By default consent is assumed granted; wire this to your CMP.

import { ga4 }        from "./providers/ga4";
import { metaPixel }  from "./providers/metaPixel";
import { EventName }  from "./events";

// Only track in production builds.
// In local dev: set REACT_APP_ANALYTICS_DEV=true in .env.local to force-enable.
const IS_ACTIVE =
  process.env.NODE_ENV === "production" ||
  process.env.REACT_APP_ANALYTICS_DEV === "true";

let _consent = true; // change to false and call setConsent(true) via CMP

export const analytics = {
  /** Call once at app startup (App.tsx useEffect). */
  init() {
    if (!IS_ACTIVE || !_consent) return;
    ga4.init();
    metaPixel.init();
  },

  /**
   * Track a page view. Pass the full path including search params so
   * /intake?service=essential is recorded separately from /intake.
   */
  page(path: string) {
    if (!IS_ACTIVE || !_consent) return;
    ga4.page(path);
    metaPixel.page(path);
  },

  /**
   * Track a named event with optional payload.
   * Use the EVENTS constants from ./events to avoid raw strings.
   */
  track(name: EventName | string, params?: Record<string, unknown>) {
    if (!IS_ACTIVE || !_consent) return;
    ga4.track(name, params);
    metaPixel.track(name, params);
  },

  /**
   * Consent gate — wire to your cookie consent banner / CMP.
   * analytics.setConsent(true)  → enable all tracking
   * analytics.setConsent(false) → disable all tracking
   */
  setConsent(value: boolean) {
    _consent = value;
  },
};

// Re-export events so callers only need one import path
export { EVENTS, EVENT_FN } from "./events";
